<script lang="ts">
	import { onDestroy } from 'svelte';
	import { EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/svelte';
	import { edgeActionBus } from '../../../stores/flow_editor';

	export let id: string;
	export let sourceX: number;
	export let sourceY: number;
	export let targetX: number;
	export let targetY: number;
	export let sourcePosition: any;
	export let targetPosition: any;
	export let style: string = '';
	export let markerEnd: string = '';
	export let data: Record<string, any> = {};
	export let source: string = '';
	export let target: string = '';
	export let animated: boolean = false;
	export let selected: boolean = false;
	export let label: string = '';
	export let labelStyle: string = '';
	export let interactionWidth: number = 20;
	export let selectable: boolean = true;
	export let deletable: boolean = true;
	export let type: string = '';
	export let sourceHandleId: string | null = null;
	export let targetHandleId: string | null = null;
	export let markerStart: string = '';

	let showMenu = false;

	let dotEl: HTMLDivElement;
	let menuPanelEl: HTMLDivElement | null = null;

	function closeMidpointMenus() {
		showMenu = false;
	}

	let detachOutside: (() => void) | undefined;

	$: {
		detachOutside?.();
		detachOutside = undefined;
		if (showMenu) {
			const fn = (e: PointerEvent) => {
				const path = e.composedPath();
				if (path.some((t) => t instanceof Node && dotEl?.contains(t))) return;
				if (menuPanelEl && path.includes(menuPanelEl)) return;
				closeMidpointMenus();
			};
			document.addEventListener('pointerdown', fn);
			detachOutside = () => document.removeEventListener('pointerdown', fn);
		}
	}

	onDestroy(() => detachOutside?.());

	$: pathOffset =
		typeof data?.pathOffset === 'number' && Number.isFinite(data.pathOffset) ? data.pathOffset : 18;

	$: [edgePath, labelX, labelY] = getSmoothStepPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
		offset: pathOffset,
	});

	function onDotClick(e: MouseEvent) {
		e.stopPropagation();
		showMenu = !showMenu;
	}

	function deleteLink(e: MouseEvent) {
		e.stopPropagation();
		closeMidpointMenus();
		edgeActionBus.set({ kind: 'delete', edgeId: id });
	}

	function insertNode(e: MouseEvent) {
		e.stopPropagation();
		closeMidpointMenus();
		edgeActionBus.set({ kind: 'requestInsert', edgeId: id, x: labelX, y: labelY });
	}

	function beginReconnectTarget(event: PointerEvent) {
		event.preventDefault();
		event.stopPropagation();
		const finish = (up: PointerEvent) => {
			window.removeEventListener('pointerup', finish);
			const element = document.elementFromPoint(up.clientX, up.clientY) as HTMLElement | null;
			const handle = element?.closest('.svelte-flow__handle.target') as HTMLElement | null;
			const nodeId = handle?.dataset.nodeid;
			if (nodeId && nodeId !== source) {
				edgeActionBus.set({
					kind: 'reconnectTarget',
					edgeId: id,
					target: nodeId,
					targetHandle: handle?.dataset.handleid || 'input',
				});
			}
		};
		window.addEventListener('pointerup', finish, { once: true });
	}
</script>

<path
	{id}
	class="svelte-flow__edge-path"
	d={edgePath}
	{style}
	marker-start={markerStart}
	marker-end={markerEnd}
	fill="none"
/>
{#if selected}
	<circle
		class="edge-reconnect-anchor nodrag nopan"
		cx={targetX}
		cy={targetY}
		r="7"
		role="button"
		aria-label="Drag to reconnect destination"
		on:pointerdown={beginReconnectTarget}
	/>
{/if}

<EdgeLabelRenderer>
	<div
		style="transform: translate(-50%, -50%) translate({labelX}px, {labelY}px);"
		class="edge-midpoint-wrapper nodrag nopan"
	>
		<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
		<div class="midpoint-dot" bind:this={dotEl} on:click={onDotClick} />
		{#if showMenu}
			<div class="midpoint-menu" bind:this={menuPanelEl}>
				<div class="mp-header-row">
					<span class="mp-header-title">Edge</span>
					<button
						type="button"
						class="mp-close"
						on:click|stopPropagation={closeMidpointMenus}
						aria-label="Close"
					>
						<i class="fa fa-times" />
					</button>
				</div>
				<button type="button" class="mp-btn delete" on:click={deleteLink}>
					<i class="fa fa-unlink" /> Delete link
				</button>
				<button type="button" class="mp-btn insert" on:click={insertNode}>
					<i class="fa fa-plus-circle" /> Insert node
				</button>
			</div>
		{/if}
	</div>
</EdgeLabelRenderer>

<style>
	.edge-reconnect-anchor {
		fill: #ffffff;
		stroke: #3b82f6;
		stroke-width: 2;
		cursor: crosshair;
		pointer-events: all;
	}
	.edge-midpoint-wrapper {
		position: absolute;
		pointer-events: all;
	}

	.midpoint-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: #4a9eff;
		border: 2px solid #fff;
		cursor: pointer;
		transition: transform 0.15s;
		box-shadow: 0 0 0 2px rgba(74, 158, 255, 0.35);
	}

	.midpoint-dot:hover {
		transform: scale(1.4);
	}

	.midpoint-menu {
		position: absolute;
		top: 18px;
		left: 50%;
		transform: translateX(-50%);
		background: #2a2a2a;
		border: 1px solid #3a3a3a;
		border-radius: 6px;
		overflow: hidden;
		z-index: 9999999;
		min-width: 168px;
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
	}

	.mp-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.35rem;
		padding: 0.35rem 0.4rem 0.35rem 0.6rem;
		border-bottom: 1px solid #3a3a3a;
		background: #252525;
	}

	.mp-header-title {
		font-size: 0.65rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #888;
	}

	.mp-close {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		padding: 0;
		border: none;
		border-radius: 4px;
		background: transparent;
		color: #9ca3af;
		cursor: pointer;
		font-size: 0.78rem;
		flex-shrink: 0;
	}

	.mp-close:hover {
		background: #3a3a3a;
		color: #e5e5e5;
	}

	.node-picker {
		max-height: 220px;
		overflow-y: auto;
	}

	.mp-btn {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		width: 100%;
		padding: 0.45rem 0.7rem;
		background: transparent;
		border: none;
		color: #e5e5e5;
		font-size: 0.82rem;
		cursor: pointer;
		text-align: left;
		white-space: nowrap;
	}

	.mp-btn:hover {
		background: #3a3a3a;
	}

	.mp-btn.delete:hover {
		color: #ef4444;
	}
</style>
